/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse} from '@angular/common/http';
import {OAuthToken, Token} from '../_models/token';

import {User} from '../_models/user';
import {UserService} from './user.service';
import {Observable, Subject} from 'rxjs';
import {
  CLIENT_ID,
  CLIENT_SECRET,
  CURRENT_USER_KEY,
  GRANT_TYPE,
  GT_PASSWORD,
  GT_REFRESH_TOKEN,
  HOME_PATH,
  PASSWORD,
  REFRESH_TOKEN,
  USERNAME
} from '../_enums/constants';
import {I18nService, WRONG_ID} from './i18n.service';
import {RESPONSE} from './abstract.service';
import {BAD_REQUEST} from '../_enums/requests';
import {Router} from '@angular/router';

@Injectable()
export class AuthService {

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                          PROPERTIES                                                                                ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public currentUser?: User = null;
  public currentToken?: Token = null;
  public returnUrl = HOME_PATH;

  private refreshSubject = new Subject<Token>();
  private refreshingToken = false;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              STATIC METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public static getOAuthBaseParams(grantType: string): HttpParams {
    return new HttpParams().set(CLIENT_ID, environment.oAuth.clientId).set(CLIENT_SECRET, environment.oAuth.client_secret).set(GRANT_TYPE, grantType);
  }

  public static getOAuthPasswordParams(username: string, password: string): HttpParams {
    return AuthService.getOAuthBaseParams(GT_PASSWORD).set(USERNAME, username).set(PASSWORD, encodeURIComponent(password));
  }

  public static getOAuthRefreshParams(refresh_token: string): HttpParams {
    return AuthService.getOAuthBaseParams(GT_REFRESH_TOKEN).set(REFRESH_TOKEN, refresh_token);
  }

  public static storeAuthToken(key: string, token: string) {
    localStorage.setItem(key, token);
  }

  public static getAuthToken(key: string): string | null {
    const item = localStorage.getItem(key);
    return item ? item : null;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              MAGICS METHODS                                                                                        ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(private httpClient: HttpClient, private i18n: I18nService, public router: Router, private userService: UserService) {
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                              VALUES GETTERS / SETTERS                                                                              ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public setCurrentToken(oAuthToken?: OAuthToken): void {
    this.currentToken = oAuthToken ? Token.fromOAuthToken(oAuthToken) : null;
    AuthService.storeAuthToken(CURRENT_USER_KEY, JSON.stringify(this.currentToken));
  }

  public getCurrentToken(): Token | null {
    if (!this.currentToken) {
      const token = JSON.parse(AuthService.getAuthToken(CURRENT_USER_KEY));
      this.currentToken = (!token) ? null : Token.fromToken(token);
    }

    return this.currentToken;
  }

  private resetAuthData(): void {
    this.setCurrentToken(null);
    this.currentUser = null;
    this.returnUrl = HOME_PATH;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                    PUBLIC METHODS                                                                                  ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public autoLogin(): Observable<User> {
    return new Observable((observer) => {
      if (this.currentUser && this.getCurrentToken()) {
        observer.next(this.currentUser);
      } else if (this.getCurrentToken() && !this.currentUser) {
        this.userService.getCurrent().subscribe((res: HttpResponse<User>) => {
          this.currentUser = res.body;
          observer.next(this.currentUser);
        }, () => {
          this.resetAuthData();
          observer.next(null);
        });
      } else {
        this.resetAuthData();
        observer.next(null);
      }
    });
  }

  public logout(): void {
    this.resetAuthData();
    location.reload();
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ///                                     POST                                                                                           ///
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  public login(username: string, password: string): Observable<User> {
    console.log("login")
    return new Observable((observer) => {
      this.httpClient.get(environment.oAuth.urlToken, {
        params: AuthService.getOAuthPasswordParams(username, password),
        observe: RESPONSE,
        reportProgress: false
      }).subscribe((res: HttpResponse<OAuthToken>) => {
        this.setCurrentToken(res.body);
        this.autoLogin().subscribe((resLogin) => observer.next(resLogin), (errLogin) => observer.error(errLogin));
      }, (err: HttpErrorResponse) => {
        observer.error(this.i18n.get(BAD_REQUEST === err.status ? WRONG_ID : err.status));
      });
    });
  }

  public refreshToken(): Observable<Token> {
    if (!this.refreshingToken) {
      const coa = this.getCurrentToken();

      if (coa && coa.refreshToken) {
        this.refreshingToken = true;

        this.httpClient.get(environment.oAuth.urlToken, {
          params: AuthService.getOAuthRefreshParams(coa.refreshToken),
          observe: RESPONSE,
          reportProgress: false
        }).subscribe((res: HttpResponse<OAuthToken>) => {
          this.setCurrentToken(res.body);
          this.refreshingToken = false;
          this.refreshSubject.next(this.getCurrentToken());
        }, () => {
          this.refreshingToken = false;
          this.refreshSubject.error(null);
        });
      } else {
        this.refreshingToken = false;
        this.refreshSubject.error(null);
      }
    }

    return this.refreshSubject.asObservable();
  }

}
